import { createContext, useContext, useEffect, useState } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { useAuthStore } from '@cleverlist/store';
import { useQueryClient } from '@tanstack/react-query';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

// Pusher must be available globally for Laravel Echo
window.Pusher = Pusher;

const EchoContext = createContext<Echo<'reverb'> | null>(null);

export function EchoProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [echo, setEcho] = useState<Echo<'reverb'> | null>(null);

  useEffect(() => {
    if (!token) {
      setEcho((prev) => {
        prev?.disconnect();
        return null;
      });
      return;
    }

    const instance = new Echo({
      broadcaster: 'reverb',
      key: import.meta.env.VITE_REVERB_APP_KEY,
      wsHost: import.meta.env.VITE_REVERB_HOST,
      wsPort: import.meta.env.VITE_REVERB_PORT ?? 80,
      wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
      forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
      enabledTransports: ['ws', 'wss'],
      authEndpoint: `${import.meta.env.VITE_API_URL}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    let hasConnected = false;
    instance.connector.pusher.connection.bind('connected', () => {
      if (hasConnected) {
        // Only invalidate on reconnect, not initial connection
        queryClient.invalidateQueries();
      }
      hasConnected = true;
    });

    setEcho(instance);

    return () => {
      instance.disconnect();
    };
  }, [token, queryClient]);

  return (
    <EchoContext.Provider value={echo}>
      {children}
    </EchoContext.Provider>
  );
}

export function useEcho(): Echo<'reverb'> | null {
  return useContext(EchoContext);
}
